import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { UsageProvider } from "./context/UsageContext";
import { FreemiumProvider } from "./context/FreemiumContext";
import { TesterModalityProvider } from "./context/TesterModalityContext";
import FreemiumLimitModal from "./components/FreemiumLimitModal";
import { trackPageview } from "./lib/analytics";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Recover from "./pages/Recover";
import Dashboard from "./pages/Dashboard";
import HomeWritingFirst from "./pages/HomeWritingFirst";
import ActivarPlan from "./pages/ActivarPlan";
import Chat from "./pages/Chat";
import Profile from "./pages/Profile";
import Tareas from "./pages/Tareas";
import Borradores from "./pages/Borradores";
import Rubricas from "./pages/Rubricas";
import Progreso from "./pages/Progreso";
import Admin from "./pages/Admin";
import AdminSoporte from "./pages/AdminSoporte";
import QuickOnboarding from "./pages/QuickOnboarding";

// Protected Route Component
// Nota: con el modelo de trial de 7 días, los usuarios con trial expirado SIGUEN ACCEDIENDO
// al dashboard, indicadores, estrategia y progreso en modo LECTURA. Las acciones premium
// (crear borradores, IA, exportaciones) se bloquean a nivel de cada componente/endpoint.
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Public Route (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/empezar" replace />;
    }

    return children;
};

function AppRoutes() {
    const location = useLocation();
    // Pageview tracking en cada navegación
    useEffect(() => {
        trackPageview();
    }, [location.pathname, location.search]);

    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/"
                element={
                    <PublicRoute>
                        <Landing />
                    </PublicRoute>
                }
            />
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />
            <Route
                path="/register"
                element={
                    <PublicRoute>
                        <Register />
                    </PublicRoute>
                }
            />
            <Route
                path="/auth/callback"
                element={<AuthCallback />}
            />
            <Route
                path="/recover"
                element={<Recover />}
            />

            {/* Protected Routes */}
            <Route
                path="/onboarding"
                element={
                    <ProtectedRoute>
                        <QuickOnboarding />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/empezar"
                element={
                    <ProtectedRoute>
                        <HomeWritingFirst />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/activar-plan"
                element={
                    <ProtectedRoute>
                        <ActivarPlan />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/chat"
                element={
                    <ProtectedRoute>
                        <Chat />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/chat/:conversationId"
                element={
                    <ProtectedRoute>
                        <Chat />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/perfil"
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/tareas"
                element={
                    <ProtectedRoute>
                        <Tareas />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/tareas/:tareaId"
                element={
                    <ProtectedRoute>
                        <Tareas />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/borradores"
                element={
                    <ProtectedRoute>
                        <Borradores />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/borradores/:draftId"
                element={
                    <ProtectedRoute>
                        <Borradores />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/rubricas"
                element={
                    <ProtectedRoute>
                        <Rubricas />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/rubricas/:rubricId"
                element={
                    <ProtectedRoute>
                        <Rubricas />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/progreso"
                element={
                    <ProtectedRoute>
                        <Progreso />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute bypassFreemium>
                        <Admin />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/soporte"
                element={
                    <ProtectedRoute bypassFreemium>
                        <AdminSoporte />
                    </ProtectedRoute>
                }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <AuthProvider>
                    <UsageProvider>
                        <TesterModalityProvider>
                            <FreemiumProvider>
                                <AppRoutes />
                                <FreemiumLimitModal />
                            </FreemiumProvider>
                        </TesterModalityProvider>
                    </UsageProvider>
                    <Toaster position="top-right" richColors closeButton />
                </AuthProvider>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
